import React, { useEffect, useRef } from 'react';
import WheelColumn from './WheelColumn';

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const daysInMonth = (month, year) => new Date(year, month, 0).getDate();

const range = (start, end) => Array.from({ length: end - start + 1 }, (_, i) => start + i);

/**
 * Three-column day/month/year wheel picker. Structurally cannot produce an
 * invalid date — the day column's option list is recomputed from the
 * selected month/year on every render, so e.g. Feb never offers day 30.
 *
 * `value` / `onChange` use { day, month, year } (month is 1-12).
 * `minYear`/`maxYear` bound the year column — pass the same 13–100 age
 * range already enforced elsewhere rather than an arbitrary span.
 */
const DateWheelPicker = ({ value, onChange, minYear, maxYear, isUnset = false }) => {
  const { day, month, year } = value;

  const dayOptions = range(1, daysInMonth(month, year)).map((d) => ({ value: d, label: String(d) }));
  const monthOptions = range(1, 12).map((m) => ({ value: m, label: MONTH_LABELS[m - 1] }));
  const yearOptions = range(minYear, maxYear).map((y) => ({ value: y, label: String(y) })).reverse();

  const clampedDay = Math.min(day, daysInMonth(month, year));

  // When the parent has no stored date yet, the wheels still show a sensible
  // default (day 1 / Jan / ~25 years ago). Report it up on mount so tapping
  // Continue without scrolling records that date instead of failing with
  // "Enter your date of birth as DD/MM/YYYY". Only when genuinely unset.
  const didMountEmit = useRef(false);
  useEffect(() => {
    if (didMountEmit.current || !isUnset) return;
    didMountEmit.current = true;
    onChange({ day: clampedDay, month, year });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      <WheelColumn
        options={dayOptions}
        value={clampedDay}
        onChange={(d) => onChange({ day: d, month, year })}
      />
      <WheelColumn
        options={monthOptions}
        value={month}
        onChange={(m) => onChange({ day: clampedDay, month: m, year })}
      />
      <WheelColumn
        options={yearOptions}
        value={year}
        onChange={(y) => onChange({ day: clampedDay, month, year: y })}
      />
    </div>
  );
};

export default DateWheelPicker;
